import React, { Component, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Colors, Spacing } from '@/shared/theme';
import { Button, Title, Text, VStack } from '@/shared/ui';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${Spacing.x6}px;
  background-color: ${Colors.background};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${Spacing.x4}px;
  color: ${Colors.error};
`;

const ErrorDetails = styled.details`
  margin-top: ${Spacing.x4}px;
  padding: ${Spacing.x3}px;
  background-color: ${Colors.backgroundAlt};
  border-radius: 8px;
  text-align: left;
  max-width: 600px;
  width: 100%;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: ${Colors.foreground};
    margin-bottom: ${Spacing.x2}px;

    &:hover {
      color: ${Colors.primary};
    }
  }
`;

const ErrorCode = styled.pre`
  font-size: 12px;
  color: ${Colors.foregroundFaint};
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

const ErrorFallbackContent: React.FC<{
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  onReset: () => void;
}> = ({ error, errorInfo, onReset }) => {
  const { t } = useTranslation();

  return (
    <ErrorContainer role="alert" aria-live="assertive">
      <VStack gap={Spacing.x4}>
        <ErrorIcon aria-hidden="true">⚠️</ErrorIcon>

        <Title size="h2" centered>
          {t('general.error.title')}
        </Title>

        <Text size="p2" centered>
          {t('general.error.description')}
        </Text>

        <Button
          onClick={onReset}
          variant="action-primary"
          text={t('general.continue')}
        />

        {import.meta.env.DEV && error && (
          <ErrorDetails>
            <summary>Error Details (Development Only)</summary>
            <ErrorCode>
              <strong>Error:</strong> {error.toString()}
              {errorInfo && (
                <>
                  {'\n\n'}
                  <strong>Component Stack:</strong>
                  {errorInfo.componentStack}
                </>
              )}
            </ErrorCode>
          </ErrorDetails>
        )}
      </VStack>
    </ErrorContainer>
  );
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, this is where you would send to error tracking service
    // e.g., Sentry, LogRocket, etc.
    this.setState({
      error,
      errorInfo,
    });
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallbackContent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.resetErrorBoundary}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
